import './EndpointSetting.css';

import React, { FormEvent, useEffect, useState } from 'react';
import { IonButtons } from '@ionic/react';
import { useUserState } from '../../hooks/context/useUserState';
import Message from '../../components/Message/Message';
import Input from '../../components/Input/Input';
import Container from '../../components/Container/Container';
import PageLayout from '../../layouts/PageLayout/PageLayout';
import PageContent from '../../layouts/PageContent/PageContent';
import Form from '../../components/Form/Form';
import { useTranslate } from '../../hooks/context/useTranslate';
import {
  BarcodeScanner,
  BarcodeFormat,
  LensFacing,
  Barcode,
} from '@capacitor-mlkit/barcode-scanning';
import { Torch } from '@capawesome/capacitor-torch';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import QrScannerUi from '../../components/QrScannerUi/QrScannerUi';
import {
  LOG_OUT,
  SET_API_ENDPOINT,
  SET_ENDPOINT_ALREADY_ENTERED,
  SET_ENDPOINT_CORRECT,
  SET_ENDPOINT_PAGE_MANUAL_ENTERING,
  SET_ENDPOINT_PAGE_STEP,
} from '../../constants';
import { getIsIos, isValidUrl, translatedToast } from '../../utils/helpers';
import { useHistory } from 'react-router';
import { useStart } from '../../api/hooks/useStart';
import { requestLogout } from '../../api/requests/requestLogout';
import Dot from '../../components/Dot/Dot';
import Button from '../../components/Button/Button';
import StepsProgress from '../../components/StepsProgress/StepsProgress';
import apiEndpoints, { updateApiEndpoint } from '../../apiEndpoints';
import ArrowIcon from '../../components/ArrowIcon/ArrowIcon';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import Loading from '../../components/Loading/Loading';
import SupportModal from '../../components/SupportModal/SupportModal';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import useRoutes from '../../hooks/useRoutes';

const EndpointSetting: React.FC = () => {
  const {
    dispatch,
    state: {
      token,
      apiEndpoint,
      branchName,
      endpointPageStep,
      endpointPageManualEntering,
      endpointAlreadyEntered,
      username,
    },
  } = useUserState();
  // When reached while logged in, this page renders inside the persistent shell, so use the content-only
  // wrapper (the shell already provides Header). When logged out, it needs its own full PageLayout.
  const Wrapper = username ? PageContent : PageLayout;
  const { t } = useTranslate();
  const handleApiError = useApiErrorHandling();
  const [url, setUrl] = useState<string>(apiEndpoint || 'https://');
  const [newBranchName, setNewBranchName] = useState<string>(branchName);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [isFlashlightAvailable, setIsFlashlightAvailable] = useState(false);
  const history = useHistory();
  const { isLoggedIn, error, success, start, isPending } = useStart();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isIos = getIsIos();
  const [isFileLoading, setIsFileLoading] = useState(false);
  const routes = useRoutes();

  const startSession = async () => {
    try {
      await start();
    } catch (e) {
      handleApiError(e);
    }
  };

  const stopScan = async () => {
    toast.remove();
    if (isFlashlightAvailable) {
      const isTorchEnabled = await Torch.isEnabled();
      if (isTorchEnabled.enabled) {
        await Torch.disable();
      }
    }
    // Make all elements in the WebView visible again
    document.querySelector('body')?.classList.remove('barcode-scanner-active');
    setIsScanning(false);
    // Remove all listeners
    await BarcodeScanner.removeAllListeners();

    // Stop the barcode scanner
    await BarcodeScanner.stopScan();
  };

  const scanSingleBarcode = async (): Promise<Barcode> => {
    return new Promise(async (resolve) => {
      document.querySelector('body')?.classList.add('barcode-scanner-active');

      const listener = await BarcodeScanner.addListener(
        'barcodesScanned',
        async (result) => {
          await listener.remove();
          document
            .querySelector('body')
            ?.classList.remove('barcode-scanner-active');
          setIsScanning(false);
          await BarcodeScanner.stopScan();
          resolve(result.barcodes[0]);
        }
      );

      await BarcodeScanner.startScan({
        formats: [BarcodeFormat.QrCode],
        lensFacing: LensFacing.Back,
      });
    });
  };

  const readBarcodeFromImage = async (): Promise<void> => {
    toast.remove();
    const { files } = await FilePicker.pickImages({ limit: 1 });
    const path = files[0]?.path;
    if (!path) {
      return;
    }
    setIsFileLoading(true);
    const { barcodes } = await BarcodeScanner.readBarcodesFromImage({
      path,
      formats: [BarcodeFormat.QrCode],
    });
    setIsFileLoading(false);
    if (barcodes && barcodes[0]) {
      if (barcodes[0].valueType === 'URL' && barcodes[0].displayValue) {
        submitUrl(barcodes[0].displayValue);
        stopScan();
      } else {
        translatedToast('toasts.invalidQR');
      }
    } else {
      translatedToast('toasts.noQRCodeInImage');
    }
  };

  const isTorchAvailable = async () => {
    const { available } = await Torch.isAvailable();
    return available;
  };

  // Will need this for improving "permissions disabled" popup
  // const openSettings = async () => {
  //   await BarcodeScanner.openSettings();
  // };

  const requestPermissions = async () => {
    const { camera } = await BarcodeScanner.requestPermissions();
    return camera;
  };

  useEffect(() => {
    const handleBackButton = (ev) => {
      ev.detail.register(10, (processNextHandler) => {
        if (apiEndpoint) {
          updateApiEndpoint(apiEndpoint);
        }
        processNextHandler();
      });
    };
    document.addEventListener('ionBackButton', handleBackButton);
    return () => {
      document.removeEventListener('ionBackButton', handleBackButton);
    };
  }, []);

  useEffect(() => {
    if (success) {
      if (isLoggedIn) {
        dispatch({ type: LOG_OUT });
      }
      dispatch({ type: SET_ENDPOINT_PAGE_STEP, payload: 2 });
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      if (isLoggedIn) {
        dispatch({ type: LOG_OUT });
        dispatch({
          type: SET_API_ENDPOINT,
          apiEndpoint: apiEndpoints.basePath,
          branchName: newBranchName,
        });
        dispatch({
          type: SET_ENDPOINT_CORRECT,
          payload: false,
        });
        updateApiEndpoint('');
      } else {
        updateApiEndpoint(apiEndpoint);
      }
    }
  }, [error]);

  async function scanQRCode() {
    const permissions = await requestPermissions();
    if (permissions === 'granted') {
      const torchAvailable = await isTorchAvailable();
      setIsFlashlightAvailable(torchAvailable);
      setIsScanning(true);
      const barcode = await scanSingleBarcode();
      if (barcode.valueType === 'URL' && barcode.displayValue) {
        submitUrl(barcode.displayValue);
      } else {
        translatedToast('toasts.invalidQR');
      }
    } else {
      translatedToast('toasts.cameraDenied');
    }
  }

  const submitUrl = async (endpoint: string) => {
    dispatch({ type: SET_ENDPOINT_ALREADY_ENTERED, payload: false });
    setUrl(endpoint);
    if (!endpoint) {
      translatedToast('toasts.missingUrl');
      return;
    }
    if (!endpoint.startsWith('https://') && !endpoint.startsWith('http://')) {
      endpoint = `https://${endpoint}`;
    }
    if (!isValidUrl(endpoint)) {
      translatedToast('toasts.invalidUrl');
      return;
    }
    if (isLoggedIn) {
      try {
        await requestLogout(token);
      } finally {
        updateApiEndpoint(endpoint);
        startSession();
      }
    } else {
      updateApiEndpoint(endpoint);
      startSession();
    }
  };

  const submitBranchName = async (e: FormEvent) => {
    e.preventDefault();
    if (!newBranchName) {
      translatedToast('toasts.missingBranchName');
      return;
    }
    dispatch({
      type: SET_API_ENDPOINT,
      apiEndpoint: apiEndpoints.basePath,
      branchName: newBranchName,
    });
    dispatch({
      type: SET_ENDPOINT_CORRECT,
      payload: true,
    });
    dispatch({ type: SET_ENDPOINT_PAGE_STEP, payload: 3 });
  };

  const enterApp = (e: FormEvent) => {
    e.preventDefault();
    dispatch({ type: SET_ENDPOINT_PAGE_STEP, payload: 1 });
    dispatch({
      type: SET_ENDPOINT_PAGE_MANUAL_ENTERING,
      payload: false,
    });
    dispatch({ type: SET_ENDPOINT_ALREADY_ENTERED, payload: true });
    history.push(routes.home());
  };

  const toggleUrlForm = () => {
    dispatch({
      type: SET_ENDPOINT_PAGE_MANUAL_ENTERING,
      payload: !endpointPageManualEntering,
    });
  };

  const manualUrlSubmit = (e: FormEvent) => {
    e.preventDefault();
    submitUrl(url);
  };

  if (isScanning) {
    return (
      <QrScannerUi
        onClose={stopScan}
        isFlashlightAvailable={isFlashlightAvailable}
        loadFromGallery={readBarcodeFromImage}
        isFileLoading={isFileLoading}
      />
    );
  }

  const onOneStepBack = () => {
    dispatch({ type: SET_ENDPOINT_PAGE_STEP, payload: endpointPageStep - 1 });
  };

  const onGoBack = () => {
    dispatch({
      type: SET_ENDPOINT_PAGE_MANUAL_ENTERING,
      payload: false,
    });
    history.goBack();
  };

  let titleLabelId, subtitleLabelId, descLabelId;
  switch (endpointPageStep) {
    case 1:
      titleLabelId = 'endpointSetting.step1.title';
      subtitleLabelId = endpointPageManualEntering
        ? 'endpointSetting.step1.subtitleManual'
        : 'endpointSetting.step1.subtitle';
      descLabelId = endpointPageManualEntering
        ? 'endpointSetting.step1.desciriptionManual'
        : 'endpointSetting.step1.desciription';
      break;
    case 2:
      titleLabelId = 'endpointSetting.step2.title';
      subtitleLabelId = 'endpointSetting.step2.subtitle';
      descLabelId = 'endpointSetting.step2.description';
      break;
    case 3:
      titleLabelId = 'endpointSetting.step3.title';
      subtitleLabelId = 'endpointSetting.step3.subtitle';
      break;
    default:
      break;
  }

  return (
    <Wrapper className="endpointPage">
      {isPending && <Loading message={t('startMessage')} />}
      <SupportModal
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
      />
      <Container className="endpointContainer">
        <h1>
          {t(titleLabelId)}
          <Dot />
        </h1>
        <StepsProgress steps={3} currentStep={endpointPageStep} />
        <Message id={subtitleLabelId} as="h2" />
        {descLabelId && <Message id={descLabelId} as="p" />}
        {endpointPageStep === 1 && !endpointPageManualEntering && (
          <>
            {/* <p>
              <a href="https://www.septim.cz/">
                <Message id="sendQRCode" />
              </a>
            </p> */}
            <Button onClick={scanQRCode} className="scanBtn">
              <Message id="scanQR" />
            </Button>
          </>
        )}

        {endpointPageStep === 1 && endpointPageManualEntering && (
          <Form
            onSubmit={manualUrlSubmit}
            submitMessage={'submit'}
            className="endpointForm"
          >
            <Input
              onChange={setUrl}
              value={url}
              name={'url'}
              label={t('url')}
            />
          </Form>
        )}
        {endpointPageStep === 2 && (
          <Form
            onSubmit={submitBranchName}
            submitMessage={'submit'}
            className="endpointForm"
          >
            <Input value={apiEndpoints.basePath} name={'url'} disabled />
            <Input
              onChange={setNewBranchName}
              value={newBranchName}
              name={'branchName'}
              label={t('branchName')}
              className="ion-margin-bottom"
            />
          </Form>
        )}
        {endpointPageStep === 3 && (
          <Form
            onSubmit={enterApp}
            submitMessage={'enterMySeptim'}
            className="endpointForm"
          >
            <Input value={apiEndpoints.basePath} name={'url'} disabled />
            <Input
              onChange={setNewBranchName}
              value={newBranchName}
              name={'branchName'}
              disabled
              className="ion-margin-bottom"
            />
          </Form>
        )}
        <div
          className={clsx('endpointFooter', isIos && 'ios-swipe-bar-padding')}
        >
          {endpointPageStep === 1 && (
            <Button
              onClick={toggleUrlForm}
              className="manualUrlBtn"
              fill="outline"
            >
              <Message
                id={endpointPageManualEntering ? 'tryScanningQR' : 'cantScanQR'}
              />
            </Button>
          )}
          <IonButtons>
            <Button onClick={() => setIsModalOpen(true)} color="primary">
              <Message id="needHelp" />
            </Button>
            {endpointPageStep > 1 && (
              <Button
                color="primary"
                className="backBtn"
                onClick={onOneStepBack}
              >
                <ArrowIcon direction="left" />
                <Message id="goBackOneStep" />
              </Button>
            )}
            {endpointPageStep === 1 && endpointAlreadyEntered && (
              <Button color="primary" className="backBtn" onClick={onGoBack}>
                <ArrowIcon direction="left" />
                <Message id="goBack" />
              </Button>
            )}
          </IonButtons>
        </div>
      </Container>
    </Wrapper>
  );
};

export default EndpointSetting;
